import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Smartphone, Monitor, Save, Loader2, Globe, Clock,
  Plus, Trash2, CalendarClock, Headphones, Signal, Settings2, X, Users, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { collection, query, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/lib/firebase';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { toast } from 'sonner';

type ReceiveMethod = 'browser' | 'phone' | 'both';
type AgentStatus = 'offline' | 'available' | 'busy' | 'away';

const AVAILABLE_LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const DAYS_I18N = {
  monday:    { en: 'Monday',    fr: 'Lundi' },
  tuesday:   { en: 'Tuesday',   fr: 'Mardi' },
  wednesday: { en: 'Wednesday', fr: 'Mercredi' },
  thursday:  { en: 'Thursday',  fr: 'Jeudi' },
  friday:    { en: 'Friday',    fr: 'Vendredi' },
  saturday:  { en: 'Saturday',  fr: 'Samedi' },
  sunday:    { en: 'Sunday',    fr: 'Dimanche' },
};

const DAY_KEYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'] as const;

const T = {
  en: {
    title: 'My Availability',
    subtitle: 'Configure your schedule, languages and call reception mode',
    statusCalls: 'Status & Calls',
    schedule: 'Schedule',
    languages: 'Languages',
    onlineStatus: 'Online Status',
    availability: 'Availability',
    enableToReceive: 'Enable to receive calls',
    currentStatus: 'Current Status',
    receiveMode: 'Receive Mode',
    receiveModeDesc: 'Choose how to receive incoming calls',
    browser: 'Browser (WebRTC)',
    browserDesc: 'Calls in the browser with headset',
    personalPhone: 'Personal Phone',
    personalPhoneDesc: 'Forward to your number',
    both: 'Both',
    bothDesc: 'Browser first, phone as backup',
    personalPhoneNumber: 'Personal phone number',
    phoneHint: 'This number will receive calls when you are available',
    weeklySchedule: 'Weekly Schedule',
    weeklyScheduleDesc: 'Set your availability hours for calls and meetings',
    timezone: 'Timezone',
    copyToAll: 'Copy to all',
    add: 'Add',
    to: 'to',
    spokenLanguages: 'Spoken Languages',
    ivrRouting: 'The IVR will route callers to you based on selected languages',
    selectAtLeastOne: 'Please select at least one language',
    saving: 'Saving...',
    saveAll: 'Save all configuration',
    savedSuccess: 'Configuration saved successfully',
    savedError: 'Error saving configuration',
    appliedToAll: 'Schedule applied to all days',
    close: 'Close',
    statusAvailable: 'Available',
    statusBusy: 'Busy',
    statusAway: 'Away',
    statusOffline: 'Offline',
  },
  fr: {
    title: 'Ma disponibilité',
    subtitle: 'Configurez votre horaire, vos langues et votre mode de réception d\'appels',
    statusCalls: 'Statut & Appels',
    schedule: 'Horaire',
    languages: 'Langues',
    onlineStatus: 'Statut en ligne',
    availability: 'Disponibilité',
    enableToReceive: 'Activez pour recevoir des appels',
    currentStatus: 'Statut actuel',
    receiveMode: 'Mode de réception',
    receiveModeDesc: 'Choisissez comment recevoir les appels entrants',
    browser: 'Navigateur (WebRTC)',
    browserDesc: 'Appels dans le navigateur avec casque',
    personalPhone: 'Téléphone personnel',
    personalPhoneDesc: 'Transfert vers votre numéro',
    both: 'Les deux',
    bothDesc: 'Navigateur en priorité, téléphone en secours',
    personalPhoneNumber: 'Numéro de téléphone personnel',
    phoneHint: 'Ce numéro recevra les appels lorsque vous êtes disponible',
    weeklySchedule: 'Horaire hebdomadaire',
    weeklyScheduleDesc: 'Définissez vos heures de disponibilité pour les appels et réunions',
    timezone: 'Fuseau horaire',
    copyToAll: 'Copier à tous',
    add: 'Ajouter',
    to: 'à',
    spokenLanguages: 'Langues parlées',
    ivrRouting: 'L\'IVR routera les appelants vers vous selon les langues sélectionnées',
    selectAtLeastOne: 'Veuillez sélectionner au moins une langue',
    saving: 'Sauvegarde...',
    saveAll: 'Sauvegarder toute la configuration',
    savedSuccess: 'Configuration sauvegardée avec succès',
    savedError: 'Erreur lors de la sauvegarde',
    appliedToAll: 'Horaire appliqué à tous les jours',
    close: 'Fermer',
    statusAvailable: 'Disponible',
    statusBusy: 'Occupé',
    statusAway: 'Absent',
    statusOffline: 'Hors ligne',
  },
};

interface TimeRange { start: string; end: string; }
interface DaySchedule { day: string; enabled: boolean; slots: TimeRange[]; }

interface AgentSettings {
  receive_method: ReceiveMethod;
  phone_number: string | null;
  is_available: boolean;
  current_status: AgentStatus;
  spoken_languages: string[];
}

export function StaffAvailabilityHub() {
  const navigate = useNavigate();
  const { user, firebaseUser } = useAuth();
  const { language } = useLanguage();
  const t = T[language as keyof typeof T] || T.en;

  const STATUS_CONFIG: Record<AgentStatus, { label: string; emoji: string; color: string }> = {
    available: { label: t.statusAvailable, emoji: '🟢', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
    busy: { label: t.statusBusy, emoji: '🔴', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
    away: { label: t.statusAway, emoji: '🟡', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
    offline: { label: t.statusOffline, emoji: '⚫', color: 'bg-gray-500/10 text-gray-400 border-gray-500/30' },
  };

  const RECEIVE_OPTIONS = [
    { value: 'browser' as const, icon: Monitor, title: t.browser, desc: t.browserDesc },
    { value: 'phone' as const, icon: Smartphone, title: t.personalPhone, desc: t.personalPhoneDesc },
    { value: 'both' as const, icon: Phone, title: t.both, desc: t.bothDesc },
  ];

  const [settings, setSettings] = useState<AgentSettings>({
    receive_method: 'browser',
    phone_number: null,
    is_available: false,
    current_status: 'offline',
    spoken_languages: ['fr'],
  });
  const [schedule, setSchedule] = useState<DaySchedule[]>(
    DAY_KEYS.map(d => ({
      day: d,
      enabled: !['saturday', 'sunday'].includes(d),
      slots: [{ start: '09:00', end: '17:00' }],
    }))
  );
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [teamStatus, setTeamStatus] = useState<Array<{ user_id: string; current_status: string; is_available: boolean; spoken_languages: string[] }>>([]);

  // Real-time team status subscription
  useEffect(() => {
    const q = query(collection(db, 'agentCallSettings'));
    const unsub = onSnapshot(q, (snap) => {
      setTeamStatus(snap.docs.map(d => ({
        user_id: d.data().userId || d.id,
        current_status: d.data().current_status || 'offline',
        is_available: d.data().is_available || false,
        spoken_languages: d.data().spoken_languages || [],
      })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if ((user?.id || user?.email) && firebaseUser) loadAll();
  }, [user?.id, user?.email, firebaseUser]);

  const loadAll = async () => {
    setIsLoading(true);
    try {
      await Promise.all([loadCallSettings(), loadSchedule()]);
    } finally {
      setIsLoading(false);
    }
  };

  //loadCallSettings

  const loadCallSettings = async () => {
  if (!user?.id) return;
  try {
    const snap = await getDoc(doc(db, 'agentCallSettings', user.id));
    if (snap.exists()) {
      const d = snap.data();
      setSettings({
        receive_method: d.receive_method || 'browser',
        phone_number: d.phone_number || null,
        is_available: d.is_available || false,
        current_status: d.current_status || 'offline',
        spoken_languages: d.spoken_languages || ['fr'],
      });
    }
  } catch (e) { console.error('Error loading call settings:', e); }
};

  const loadSchedule = async () => {
    if (!user?.id) return;
    try {
      const snap = await getDoc(doc(db, 'userAvailability', user.id));
      if (snap.exists()) {
        const data = snap.data();
        if (data.schedule) setSchedule(data.schedule);
        if (data.timezone) setTimezone(data.timezone);
      }
    } catch (e) {
      console.error('Error loading schedule:', e);
    }
  };

  const saveAgentSettingsViaEdge = async (payload: Record<string, any>) => {
  if (!user?.id) throw new Error('No user');
  await setDoc(doc(db, 'agentCallSettings', user.id), {
    ...payload, userId: user.id, updatedAt: serverTimestamp(),
  }, { merge: true });
};
   
  const saveAll = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    try {
      // Save agent call settings via edge function (bypasses RLS)
      await saveAgentSettingsViaEdge({
        receive_method: settings.receive_method,
        phone_number: settings.phone_number,
        is_available: settings.is_available,
        current_status: settings.current_status,
        spoken_languages: settings.spoken_languages,
      });

    // Convertit le format "schedule" (jour en texte) vers le format "slots"
      // (dayOfWeek numérique) attendu par useBooking.ts, getPublicStaffAvailability
      // et bookStaffCall — sinon /contact ne trouve jamais ces disponibilités.
      const DAY_TO_NUMBER: Record<string, number> = {
        sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6,
      };
      const flatSlots = schedule
        .filter((d) => d.enabled)
        .flatMap((d) =>
          d.slots.map((s) => ({
            dayOfWeek: DAY_TO_NUMBER[d.day],
            startTime: s.start,
            endTime: s.end,
          }))
        );

      const availData = {
        timezone,
        schedule,          // conservé pour l'affichage de cet onglet
        slots: flatSlots,  // format canonique utilisé par /contact et /book/:hostId
        slotDuration: 30,
        bufferBetweenMeetings: 15,
        userId: user.id,
        role: (user as any)?.roles?.[0] || '',
        displayName: user.displayName || (language === 'fr' ? 'Membre de l\'équipe' : 'Team Member'),
name: user.displayName || (language === 'fr' ? 'Membre de l\'équipe' : 'Team Member'),
      };
      const docRef = doc(db, 'userAvailability', user.id);
      await setDoc(docRef, availData, { merge: true });

      toast.success(t.savedSuccess);
    } catch (e) {
      console.error(e);
      toast.error(t.savedError);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleAvailability = async (available: boolean) => {
    const newStatus: AgentStatus = available ? 'available' : 'offline';
    setSettings(prev => ({ ...prev, is_available: available, current_status: newStatus }));
    if (user?.id) {
      try {
        await saveAgentSettingsViaEdge({
          is_available: available,
          current_status: newStatus,
        });
      } catch (e) {
        console.error('Toggle availability error:', e);
      }
    }
  };

  const formatPhone = (v: string) => {
    const c = v.replace(/\D/g, '');
    if (c.length >= 6) return `(${c.slice(0, 3)}) ${c.slice(3, 6)}-${c.slice(6, 10)}`;
    if (c.length >= 3) return `(${c.slice(0, 3)}) ${c.slice(3)}`;
    return c;
  };

  const toggleDay = (i: number) => {
    const s = [...schedule];
    s[i].enabled = !s[i].enabled;
    setSchedule(s);
  };

  const updateSlot = (di: number, si: number, field: 'start' | 'end', value: string) => {
    const s = [...schedule];
    s[di].slots[si][field] = value;
    setSchedule(s);
  };

  const addSlot = (di: number) => {
    const s = [...schedule];
    s[di].slots.push({ start: '13:00', end: '17:00' });
    setSchedule(s);
  };

  const removeSlot = (di: number, si: number) => {
    const s = [...schedule];
    s[di].slots.splice(si, 1);
    setSchedule(s);
  };

  const applyToAllDays = (source: DaySchedule) => {
    setSchedule(prev => prev.map(d => ({ ...d, enabled: true, slots: [...source.slots.map(s => ({ ...s }))] })));
    toast.info(t.appliedToAll);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[settings.current_status];
  const lang = language as 'en' | 'fr';

  return (
    <div className="space-y-6">
      {/* Header with live status */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6 text-amber-500" />
            {t.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`${statusConf.color} border px-3 py-1`}>
            {statusConf.emoji} {statusConf.label}
          </Badge>
          {settings.current_status !== 'available' && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-xs border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
              onClick={async () => {
                try {
                  await saveAgentSettingsViaEdge({
                    is_available: true,
                    current_status: 'available',
                  });
                  setSettings(p => ({ ...p, is_available: true, current_status: 'available' }));
                  toast.success(language === 'fr' ? 'Statut réinitialisé à Disponible' : 'Status reset to Available');
                } catch (e) {
                  console.error(e);
                  toast.error(language === 'fr' ? 'Erreur de réinitialisation' : 'Reset error');
                }
              }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {language === 'fr' ? 'Réinitialiser' : 'Reset'}
            </Button>
          )}
          <Switch
            checked={settings.is_available}
            onCheckedChange={toggleAvailability}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            title={t.close}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Live Team Status */}
      {teamStatus.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium">
                {language === 'fr' ? 'Statut de l\'équipe en direct' : 'Live Team Status'}
              </span>
              <Badge variant="outline" className="ml-auto text-xs animate-pulse">
                {language === 'fr' ? 'Temps réel' : 'Real-time'}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-3">
              {teamStatus.map(agent => {
                const isSelf = agent.user_id === user?.id;
                const emoji = { available: '🟢', busy: '🔴', away: '🟡', offline: '⚫' }[agent.current_status] || '⚫';
                const label = STATUS_CONFIG[agent.current_status as AgentStatus]?.label || agent.current_status;
                return (
                  <div key={agent.user_id} className={`flex items-center gap-1.5 text-sm px-2 py-1 rounded-md ${isSelf ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-muted/50'}`}>
                    <Avatar className="h-5 w-5">
                      <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                        {agent.user_id.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span>{emoji} {label}</span>
                    {isSelf && <span className="text-[10px] text-muted-foreground">({language === 'fr' ? 'vous' : 'you'})</span>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="status" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="status" className="gap-2">
            <Signal className="h-4 w-4" />
            {t.statusCalls}
          </TabsTrigger>
          <TabsTrigger value="schedule" className="gap-2">
            <CalendarClock className="h-4 w-4" />
            {t.schedule}
          </TabsTrigger>
          <TabsTrigger value="languages" className="gap-2">
            <Globe className="h-4 w-4" />
            {t.languages}
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Status & Call Settings ── */}
        <TabsContent value="status" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Signal className="h-5 w-5 text-amber-500" />
                {t.onlineStatus}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">{t.availability}</Label>
                  <p className="text-sm text-muted-foreground">{t.enableToReceive}</p>
                </div>
                <Switch checked={settings.is_available} onCheckedChange={toggleAvailability} />
              </div>
              <div className="space-y-2">
                <Label>{t.currentStatus}</Label>
                <Select
                  value={settings.current_status}
                  onValueChange={(v: AgentStatus) => setSettings(p => ({ ...p, current_status: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.emoji} {v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Headphones className="h-5 w-5 text-amber-500" />
                {t.receiveMode}
              </CardTitle>
              <CardDescription>{t.receiveModeDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={settings.receive_method}
                onValueChange={(v: ReceiveMethod) => setSettings(p => ({ ...p, receive_method: v }))}
                className="grid gap-3"
              >
                {RECEIVE_OPTIONS.map(opt => (
                  <div key={opt.value} className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer">
                    <RadioGroupItem value={opt.value} id={`rm-${opt.value}`} />
                    <Label htmlFor={`rm-${opt.value}`} className="flex items-center gap-3 cursor-pointer flex-1">
                      <opt.icon className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium">{opt.title}</p>
                        <p className="text-sm text-muted-foreground">{opt.desc}</p>
                      </div>
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              {(settings.receive_method === 'phone' || settings.receive_method === 'both') && (
                <div className="space-y-2 pt-2">
                  <Label>{t.personalPhoneNumber}</Label>
                  <Input
                    type="tel"
                    placeholder="(514) 555-1234"
                    value={settings.phone_number || ''}
                    onChange={(e) => setSettings(p => ({ ...p, phone_number: formatPhone(e.target.value) }))}
                  />
                  <p className="text-xs text-muted-foreground">{t.phoneHint}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 2: Weekly Schedule ── */}
        <TabsContent value="schedule" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-amber-500" />
                {t.weeklySchedule}
              </CardTitle>
              <CardDescription>{t.weeklyScheduleDesc}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-2 mb-4">
                <Label>{t.timezone}</Label>
                <Input
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="max-w-sm"
                />
              </div>
              <Separator />
              <div className="space-y-3 pt-3">
                {schedule.map((ds, di) => (
                  <div key={ds.day} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <Switch checked={ds.enabled} onCheckedChange={() => toggleDay(di)} />
                        <Label className="font-medium">
                          {DAYS_I18N[DAY_KEYS[di]][lang] || DAYS_I18N[DAY_KEYS[di]].en}
                        </Label>
                      </div>
                      {ds.enabled && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => applyToAllDays(ds)} className="text-xs">
                            {t.copyToAll}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => addSlot(di)}>
                            <Plus className="h-3 w-3 mr-1" />
                            {t.add}
                          </Button>
                        </div>
                      )}
                    </div>
                    {ds.enabled && (
                      <div className="space-y-2 ml-12">
                        {ds.slots.map((slot, si) => (
                          <div key={si} className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <Input type="time" value={slot.start} onChange={(e) => updateSlot(di, si, 'start', e.target.value)} className="w-28" />
                            <span className="text-muted-foreground text-sm">{t.to}</span>
                            <Input type="time" value={slot.end} onChange={(e) => updateSlot(di, si, 'end', e.target.value)} className="w-28" />
                            {ds.slots.length > 1 && (
                              <Button variant="ghost" size="icon" onClick={() => removeSlot(di, si)} className="h-8 w-8">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 3: Languages ── */}
        <TabsContent value="languages" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-5 w-5 text-amber-500" />
                {t.spokenLanguages}
              </CardTitle>
              <CardDescription>{t.ivrRouting}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {AVAILABLE_LANGUAGES.map(lang => (
                  <div key={lang.code} className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-muted/50">
                    <Checkbox
                      id={`hub-lang-${lang.code}`}
                      checked={settings.spoken_languages.includes(lang.code)}
                      onCheckedChange={(checked) => {
                        setSettings(p => ({
                          ...p,
                          spoken_languages: checked
                            ? [...p.spoken_languages, lang.code]
                            : p.spoken_languages.filter(l => l !== lang.code),
                        }));
                      }}
                    />
                    <Label htmlFor={`hub-lang-${lang.code}`} className="cursor-pointer flex items-center gap-3 flex-1">
                      <span className="text-2xl">{lang.flag}</span>
                      <span className="font-medium">{lang.label}</span>
                    </Label>
                  </div>
                ))}
              </div>
              {settings.spoken_languages.length === 0 && (
                <p className="text-xs text-destructive mt-2">{t.selectAtLeastOne}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Global Save */}
      <div className="flex justify-end pt-2">
        <Button onClick={saveAll} disabled={isSaving} size="lg" className="gap-2">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isSaving ? t.saving : t.saveAll}
        </Button>
      </div>
    </div>
  );
}
